import { IPrintParams } from "../components/table/FlightTableGrid"

export default function getDocDefinition(
  printParams: IPrintParams,
  agGridApi,
  agGridColumnApi
) {
  const {
    PDF_HEADER_COLOR,
    PDF_INNER_BORDER_COLOR,
    PDF_OUTER_BORDER_COLOR,
    PDF_ODD_BKG_COLOR,
    PDF_EVEN_BKG_COLOR,
    PDF_HEADER_HEIGHT,
    PDF_ROW_HEIGHT,
    PDF_PAGE_ORITENTATION,
    PDF_WITH_CELL_FORMATTING,
    PDF_WITH_FOOTER_PAGE_COUNT
  } = printParams

  return (function () {
    const columnGroupsToExport = getColumnGroupsToExport()

    const columnsToExport = getColumnsToExport()

    const widths = getExportedColumnsWidths(columnsToExport)

    const rowsToExport = getRowsToExport(columnsToExport)

    const body = columnGroupsToExport
      ? [columnGroupsToExport, columnsToExport, ...rowsToExport]
      : [columnsToExport, ...rowsToExport]

    const headerRows = columnGroupsToExport ? 2 : 1

    const footer = PDF_WITH_FOOTER_PAGE_COUNT
      ? function (currentPage, pageCount) {
          return {
            text: currentPage.toString() + " of " + pageCount,
            margin: [10],
            fontSize: 8
          }
        }
      : null

    const pageMargins = [10, 20, 10, PDF_WITH_FOOTER_PAGE_COUNT ? 20 : 10]

    const heights = (rowIndex: number) =>
      rowIndex < headerRows ? PDF_HEADER_HEIGHT : PDF_ROW_HEIGHT

    const fillColor = (rowIndex: number, node) => {
      if (rowIndex < node.table.headerRows) {
        return PDF_HEADER_COLOR
      }
      return rowIndex % 2 === 0 ? PDF_ODD_BKG_COLOR : PDF_EVEN_BKG_COLOR
    }

    const hLineWidth = (i: number, node) =>
      i === 0 || i === node.table.body.length ? 1 : 1

    const vLineWidth = (i: number, node) =>
      i === 0 || i === node.table.widths.length ? 1 : 0

    const hLineColor = (i: number, node) =>
      i === 0 || i === node.table.body.length
        ? PDF_OUTER_BORDER_COLOR
        : PDF_INNER_BORDER_COLOR

    const vLineColor = (i: number, node) =>
      i === 0 || i === node.table.widths.length
        ? PDF_OUTER_BORDER_COLOR
        : PDF_INNER_BORDER_COLOR

    const docDefintiion = {
      pageOrientation: PDF_PAGE_ORITENTATION,

      footer,
      content: [
        {
          style: "myTable",
          table: {
            headerRows,
            widths,
            body,
            heights
          },
          layout: {
            fillColor,
            hLineWidth,
            vLineWidth,
            hLineColor,
            vLineColor
          }
        }
      ],

      styles: {
        myTable: {
          margin: [0, 0, 0, 0],
          fontSize: 8
        },
        tableHeader: {
          bold: true,
          color: PDF_EVEN_BKG_COLOR,
          margin: [-3, 5, -5, 2]
        },
        tableCell: {
          margin: [-2, 0, -3, 0]
        }
      },
      pageMargins
    }

    return docDefintiion
  })()

  function getColumnGroupsToExport() {
    let displayedColumnGroups = agGridColumnApi.getAllDisplayedColumnGroups()

    let isColumnGrouping = displayedColumnGroups.some((col) =>
      col.hasOwnProperty("children")
    )

    if (!isColumnGrouping) {
      return null
    }

    let columnGroupsToExport = []

    displayedColumnGroups.forEach((colGroup) => {
      let numberOfEmptyHeaderCellsToAdd = 0

      // add an empty header cell now for every column being spanned
      colGroup.displayedChildren.forEach((childCol) => {
        let pdfExportOptions = getPdfExportOptions(childCol.getColId())
        if (!pdfExportOptions || !pdfExportOptions.skipColumn) {
          numberOfEmptyHeaderCellsToAdd++
        }
      })

      for (let i = 0; i < numberOfEmptyHeaderCellsToAdd; i++) {
        columnGroupsToExport.push({})
      }
    })

    return columnGroupsToExport
  }

  function getColumnsToExport() {
    let columnsToExport = []

    agGridColumnApi.getAllDisplayedColumns().forEach((col) => {
      let pdfExportOptions = getPdfExportOptions(col.getColId())
      if (pdfExportOptions && pdfExportOptions.skipColumn) {
        return
      }
      let headerCell = createHeaderCell(col)
      columnsToExport.push(headerCell)
    })

    return columnsToExport
  }

  function getRowsToExport(columnsToExport) {
    let rowsToExport = []

    agGridApi.forEachNodeAfterFilterAndSort((node) => {
      let rowToExport = columnsToExport.map(({ colId }) => {
        let cellValue = agGridApi.getValue(colId, node)
        let tableCell = createTableCell(cellValue, colId)
        return tableCell
      })
      rowsToExport.push(rowToExport)
    })

    return rowsToExport
  }

  function getExportedColumnsWidths(columnsToExport) {
    return columnsToExport.map(() => 100 / columnsToExport.length + "%")
  }

  function createHeaderCell(col) {
    let headerCell = { text: "", colId: "" }

    let headerName = col.colDef.headerName

    if (col.sort) {
      headerName += ` (${col.sort})`
    }
    if (col.filterActive) {
      headerName += ` [FILTERING]`
    }

    headerCell.text = headerName
    headerCell.colId = col.getColId()

    headerCell["style"] = "tableHeader"

    return headerCell
  }

  function createTableCell(cellValue, colId) {
    const tableCell = {
      text: cellValue !== undefined ? cellValue : "",
      style: "tableCell"
    }

    const pdfExportOptions = getPdfExportOptions(colId)

    if (pdfExportOptions) {
      const { styles } = pdfExportOptions

      if (PDF_WITH_CELL_FORMATTING && styles) {
        Object.entries(styles).forEach(
          ([key, value]) => (tableCell[key] = value)
        )
      }
    }

    return tableCell
  }

  function getPdfExportOptions(colId) {
    let col = agGridColumnApi.getColumn(colId)
    return col.colDef.pdfExportOptions
  }
}
